import { type Accessor, createSignal, type Setter } from "solid-js";

// Granular stores for fine-grained reactivity
// Each operation and block gets its own signal for optimal performance

// Block delta subscription system - pure event-based, no reactivity
type DeltaSubscriber = (delta: string) => void;
type BlockStopSubscriber = () => void;

const blockDeltaSubscribers = new Map<string, Set<DeltaSubscriber>>();
const blockStopSubscribers = new Map<string, Set<BlockStopSubscriber>>();

// Content accumulator for blocks - uses array of chunks to avoid O(n²) string concatenation
const blockContentChunks = new Map<string, string[]>();

// Operation delta timestamp tracking - when did we last receive a delta for any block in this operation
const operationLastDeltaTimestamps = new Map<string, [Accessor<number>, Setter<number>]>();

// Block to operation mapping for looking up operationId from blockId
const blockToOperationMap = new Map<string, string>();

// RAF-batched timestamp updates to avoid signal cascade on every delta
const pendingTimestampUpdates = new Set<string>();
let timestampRafId: number | undefined;

// Update the delta timestamp for an operation (batched via RAF to ~60fps)
export const updateOperationDeltaTimestamp = (operationId: string): void => {
  pendingTimestampUpdates.add(operationId);

  if (timestampRafId === undefined) {
    timestampRafId = requestAnimationFrame(() => {
      timestampRafId = undefined;
      const now = Date.now();
      for (const opId of pendingTimestampUpdates) {
        const timestamp = operationLastDeltaTimestamps.get(opId);
        if (timestamp) {
          timestamp[1](now);
        } else {
          operationLastDeltaTimestamps.set(opId, createSignal(now));
        }
      }
      pendingTimestampUpdates.clear();
    });
  }
};

/**
 * Cancel any pending timestamp updates.
 * Call this when cleaning up a streaming chat session.
 */
const cancelPendingTimestampUpdates = (): void => {
  if (timestampRafId !== undefined) {
    cancelAnimationFrame(timestampRafId);
    timestampRafId = undefined;
  }
  pendingTimestampUpdates.clear();
};

export const getOperationLastDeltaTimestamp = (operationId: string): Accessor<number> => {
  let timestamp = operationLastDeltaTimestamps.get(operationId);
  if (!timestamp) {
    timestamp = createSignal(0);
    operationLastDeltaTimestamps.set(operationId, timestamp);
  }
  return timestamp[0];
};

export const emitBlockDelta = (blockId: string, delta: string, operationId?: string): void => {
  // Store chunks for late subscribers - O(1) push instead of O(n) string concat
  const chunks = blockContentChunks.get(blockId);
  if (chunks) {
    chunks.push(delta);
  } else {
    blockContentChunks.set(blockId, [delta]);
  }

  if (operationId) {
    blockToOperationMap.set(blockId, operationId);
    updateOperationDeltaTimestamp(operationId);
  } else {
    const storedOperationId = blockToOperationMap.get(blockId);
    if (storedOperationId) {
      updateOperationDeltaTimestamp(storedOperationId);
    }
  }

  const subscribers = blockDeltaSubscribers.get(blockId);

  if (subscribers && delta) {
    subscribers.forEach((subscriber) => {
      try {
        subscriber(delta);
      } catch (error) {
        console.warn("Error in delta subscriber:", error);
      }
    });
  }
};

export const emitBlockStop = (blockId: string): void => {
  const subscribers = blockStopSubscribers.get(blockId);
  if (subscribers) {
    subscribers.forEach((subscriber) => {
      try {
        subscriber();
      } catch (error) {
        console.warn("Error in block stop subscriber:", error);
      }
    });
  }

  // Immediate cleanup - free memory as soon as block completes
  blockContentChunks.delete(blockId);
  blockDeltaSubscribers.delete(blockId);
  blockStopSubscribers.delete(blockId);
  blockToOperationMap.delete(blockId);
};

interface SubscribeOptions {
  // Hand the subscriber the chunks that arrived before it subscribed, ahead of any live delta.
  replay?: boolean;
}

export const subscribeToBlockDeltas = (
  blockId: string,
  deltaSubscriber: DeltaSubscriber,
  options?: SubscribeOptions
): (() => void) => {
  // The backlog goes out synchronously, before the subscriber joins the live set. Pacing it over
  // animation frames left a window where a live delta overtook older chunks, splicing the text.
  // It goes in two calls, not one per chunk, since each call is a markdown reparse and repaint:
  // the first chunk alone, because renderers decide on it whether to unwrap a JSON envelope,
  // then everything after it joined.
  const backlog = options?.replay ? blockContentChunks.get(blockId) : undefined;
  const firstChunk = backlog?.[0];
  if (backlog && firstChunk !== undefined) {
    deltaSubscriber(firstChunk);
    if (backlog.length > 1) deltaSubscriber(backlog.slice(1).join(""));
  }

  let subscribers = blockDeltaSubscribers.get(blockId);
  if (!subscribers) {
    subscribers = new Set();
    blockDeltaSubscribers.set(blockId, subscribers);
  }
  subscribers.add(deltaSubscriber);

  return () => {
    subscribers.delete(deltaSubscriber);
    if (subscribers.size === 0) {
      blockDeltaSubscribers.delete(blockId);
    }
  };
};

export const subscribeToBlockStop = (blockId: string, stopSubscriber: BlockStopSubscriber): (() => void) => {
  let subscribers = blockStopSubscribers.get(blockId);
  if (!subscribers) {
    subscribers = new Set();
    blockStopSubscribers.set(blockId, subscribers);
  }
  subscribers.add(stopSubscriber);

  return () => {
    subscribers.delete(stopSubscriber);
    if (subscribers.size === 0) {
      blockStopSubscribers.delete(blockId);
    }
  };
};

export const getBlockContent = (blockId: string): string => {
  const chunks = blockContentChunks.get(blockId);
  return chunks ? chunks.join("") : "";
};

/**
 * A streaming block's first non-blank line, read across its chunks without joining them, which a
 * caller wanting a title or a one-line label would otherwise pay for on every read of a growing
 * document. `ended` is false while that line is still the last one received: more of it may come.
 */
export const getBlockFirstLine = (blockId: string): { text: string; ended: boolean } => {
  let line = "";
  for (const chunk of blockContentChunks.get(blockId) ?? []) {
    let start = 0;
    for (let end = chunk.indexOf("\n"); end !== -1; end = chunk.indexOf("\n", start)) {
      line += chunk.slice(start, end);
      if (line.trim()) return { text: line, ended: true };
      line = "";
      start = end + 1;
    }
    line += chunk.slice(start);
  }
  return { text: line.trim() ? line : "", ended: false };
};

/**
 * Clear only the accumulated block content chunks. Use this before replaying
 * a stream to prevent duplicate content, without dropping the delta and stop
 * subscriptions active UI components hold.
 */
export const clearBlockContentChunks = (): void => {
  blockContentChunks.clear();
};

/**
 * Clear the granular state of these blocks and operations only — for a chat
 * dropped while another stays on screen, whose blocks must keep theirs.
 */
export const clearGranularStoresFor = (blockIds: Iterable<string>, operationIds: Iterable<string>): void => {
  for (const id of blockIds) {
    blockDeltaSubscribers.delete(id);
    blockStopSubscribers.delete(id);
    blockContentChunks.delete(id);
    blockToOperationMap.delete(id);
  }
  for (const id of operationIds) {
    operationLastDeltaTimestamps.delete(id);
    pendingTimestampUpdates.delete(id);
  }
};

/**
 * Clear all granular stores - call this when completely ending a chat session
 * or navigating away from the chat. This frees all memory held by module-level state.
 */
export const clearAllGranularStores = (): void => {
  cancelPendingTimestampUpdates();

  operationLastDeltaTimestamps.clear();

  blockDeltaSubscribers.clear();
  blockStopSubscribers.clear();
  blockContentChunks.clear();
  blockToOperationMap.clear();
};
